import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { api } from '../shared/api';
import { formatDateTime } from '../shared/timezone';

export function UsageAnalyticsPage({ user, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('daily');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await api.get('/usage/analytics');
      if (json.success) {
        setData(json.data);
      } else {
        setError(json.error || 'Daten konnten nicht geladen werden');
      }
    } catch (e) {

      setError('Verbindungsfehler');
    }
    setLoading(false);
  }, []);

  const syncData = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/elevenlabs/sync-usage');
      setSyncResult(res);
      if (res.synced > 0) {
        await loadAnalytics();
      }
    } catch (e) {
      setSyncResult({ success: false, message: 'Synchronisierung fehlgeschlagen: ' + String(e) });
    }
    setSyncing(false);
  };

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-3">{error || 'Nutzungsdaten konnten nicht geladen werden.'}</p>
        <button 
          onClick={loadAnalytics}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          data-testid="retry-btn"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  const { credits, overview, per_agent, daily, weekly } = data;

  const creditColor = credits.status === 'empty' ? 'red' : credits.status === 'critical' ? 'red' : credits.status === 'low' ? 'yellow' : 'blue';
  const creditBarColor = creditColor === 'red' ? 'bg-red-500' : creditColor === 'yellow' ? 'bg-yellow-500' : 'bg-blue-500';
  const creditBgColor = creditColor === 'red' ? 'bg-red-50 border-red-200' : creditColor === 'yellow' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200';

  // Last 14 days for chart
  const chartData = view === 'daily' ? daily.slice(-14) : weekly;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="usage-analytics-page">
      {/* Credit Overview */}
      <div className={`rounded-xl border p-4 sm:p-6 ${creditBgColor}`} data-testid="credit-overview">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Verbrauchte Credits</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {credits.used} von {credits.total} Minuten verbraucht
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-800">
              {credits.remaining} <span className="text-base font-normal text-gray-500">Min</span>
            </p>
            <p className="text-xs text-gray-500">verbleibend</p>
          </div>
        </div>
        <div className="mt-4 w-full h-3 bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${creditBarColor}`}
            style={{ width: `${Math.min(100, credits.percentage_used)}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-xs text-gray-500">
          <span>{credits.percentage_used}% verbraucht</span>
          {credits.status !== 'ok' && (
            <button
              onClick={() => onNavigate && onNavigate('billing')}
              className="text-blue-600 font-medium hover:underline"
              data-testid="upgrade-link"
            >
              Plan upgraden
            </button>
          )}
        </div>
      </div>

      {/* Sync Button */}
      <div className="bg-white rounded-xl border p-4 sm:p-6" data-testid="sync-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Daten synchronisieren</h3>
            <p className="text-xs text-gray-500 mt-0.5">Historische Anrufdaten von ElevenLabs importieren</p>
          </div>
          <button
            onClick={syncData}
            disabled={syncing}
            className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors flex items-center gap-2 shrink-0"
            data-testid="sync-usage-btn"
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                Synchronisiere...
              </>
            ) : 'Jetzt synchronisieren'}
          </button>
        </div>
        {syncResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${syncResult.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {syncResult.message}
            {syncResult.agents && syncResult.agents.length > 0 && (
              <ul className="mt-1 text-xs space-y-0.5">
                {syncResult.agents.map((a, i) => (
                  <li key={i}>{a.agent_name}: {a.synced} Gespräche ({Math.round(a.duration_seconds / 60)} Min)</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Gesamte Anrufe', value: overview.total_calls, sub: 'Alle Agenten', icon: '📞' },
          { label: 'Gesprächszeit', value: `${overview.total_minutes}`, sub: 'Minuten gesamt', icon: '⏱️' },
          { label: 'Durchschnitt', value: `${overview.avg_duration_minutes}`, sub: 'Min / Anruf', icon: '📊' },
          { label: 'Agenten', value: overview.agents_count, sub: 'Konfiguriert', icon: '🤖' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl border p-4" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl">{stat.icon}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
            <p className="text-xs text-gray-400">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Usage Chart */}
      <div className="bg-white rounded-xl border p-4 sm:p-6" data-testid="usage-chart">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <h3 className="text-base font-semibold text-gray-800">Nutzungsverlauf</h3>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('daily')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                view === 'daily' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
              }`}
              data-testid="view-daily-btn"
            >
              Letzte 14 Tage
            </button>
            <button
              onClick={() => setView('weekly')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                view === 'weekly' ? 'bg-white shadow text-gray-800' : 'text-gray-500'
              }`}
              data-testid="view-weekly-btn"
            >
              Letzte 4 Wochen
            </button>
          </div>
        </div>

        <div className="h-64 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            {view === 'daily' ? (
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="minutesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const parts = d.split('-');
                    return `${parts[2]}.${parts[1]}`;
                  }}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    background: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                  }}
                  labelFormatter={(d) => {
                    const parts = d.split('-');
                    return `${parts[2]}.${parts[1]}.${parts[0]}`;
                  }}
                  formatter={(value, name) => [
                    `${typeof value === 'number' ? value.toFixed(1) : value}`,
                    name === 'minutes' ? 'Minuten' : 'Anrufe'
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="minutes"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#minutesGradient)"
                  name="minutes"
                />
              </AreaChart>
            ) : (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="week_start"
                  tickFormatter={(d) => `KW ${d}`}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    background: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                  }}
                  labelFormatter={(d) => `Woche ${d}`}
                  formatter={(value, name) => [
                    `${typeof value === 'number' ? value.toFixed(1) : value}`,
                    name === 'minutes' ? 'Minuten' : 'Anrufe'
                  ]}
                />
                <Bar dataKey="minutes" fill="#3b82f6" radius={[6, 6, 0, 0]} name="minutes" />
                <Bar dataKey="calls" fill="#93c5fd" radius={[6, 6, 0, 0]} name="calls" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per Agent Breakdown */}
      <div className="bg-white rounded-xl border" data-testid="agent-breakdown">
        <div className="p-4 sm:p-6 border-b">
          <h3 className="text-base font-semibold text-gray-800">Nutzung pro Agent</h3>
          <p className="text-xs text-gray-500 mt-0.5">Aufschlüsselung nach Agenten</p>
        </div>

        {per_agent.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            Noch keine Anrufdaten vorhanden.
          </div>
        ) : (
          <div className="divide-y">
            {per_agent.map((agent) => {
              const pct = overview.total_minutes > 0
                ? (agent.total_minutes / overview.total_minutes) * 100
                : 0;
              return (
                <div key={agent.agent_id} className="p-4 sm:px-6 hover:bg-gray-50 transition-colors" data-testid={`agent-usage-${agent.agent_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${agent.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{agent.agent_name}</p>
                        <p className="text-xs text-gray-400">
                          {agent.last_call
                            ? `Letzter Anruf: ${formatDateTime(agent.last_call)}`
                            : 'Noch keine Anrufe'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{agent.total_calls}</p>
                        <p className="text-xs text-gray-400">Anrufe</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{agent.total_minutes}</p>
                        <p className="text-xs text-gray-400">Minuten</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(1, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Weekly Summary Cards */}
      <div className="bg-white rounded-xl border p-4 sm:p-6" data-testid="weekly-summary">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Wochenvergleich</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {weekly.map((w, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400 mb-1">{w.week_start} - {w.week_end}</p>
              <p className="text-lg font-bold text-gray-800">{w.minutes}</p>
              <p className="text-xs text-gray-500">Min / {w.calls} Anrufe</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
